import CloseIcon from '@mui/icons-material/Close';
import {
  alpha,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  OutlinedInput,
  Typography,
  useTheme,
} from '@mui/material';
import { FC, useEffect, useMemo, useState } from 'react';
import { FooterButtons } from '../../components/Dialog/ConfirmDialog/FooterButtons';
import { DIALOG_PX, dialogInputSx, dialogPaperSx } from './sourcing-dialog.styles';

interface DuplicateListDialogProps {
  open: boolean;
  loading?: boolean;
  originalName: string;
  /** Names of every list already loaded — used for the client-side dup check. */
  existingNames?: string[];
  /** Error from a 409 LIST_NAME_TAKEN that slipped past the cached check. */
  serverError?: string | null;
  onCancel: () => void;
  onConfirm: (name: string) => void;
}

export const DuplicateListDialog: FC<DuplicateListDialogProps> = ({
  open,
  loading,
  originalName,
  existingNames = [],
  serverError,
  onCancel,
  onConfirm,
}) => {
  const { colors } = useTheme();
  const [name, setName] = useState('');
  // Local copy of the parent's 409 message so it can be dismissed as soon as
  // the user starts typing a new name — otherwise the banner keeps pointing
  // at the old colliding value while the user is mid-correction.
  const [activeServerError, setActiveServerError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setName(`${originalName} (Copy)`);
    }
  }, [open, originalName]);

  useEffect(() => {
    setActiveServerError(serverError ?? null);
  }, [serverError]);

  // Pre-normalize once per re-render rather than on every keystroke.
  const takenNamesLower = useMemo(
    () => new Set(existingNames.map((n) => n.trim().toLowerCase())),
    [existingNames]
  );

  const trimmedName = name.trim();
  const isEmpty = trimmedName.length === 0;
  const isClientCollision = !isEmpty && takenNamesLower.has(trimmedName.toLowerCase());
  const inlineError =
    activeServerError ?? (isClientCollision ? `A list named "${trimmedName}" already exists.` : null);

  const handleNameChange = (next: string) => {
    setName(next);
    setActiveServerError(null);
  };

  const handleConfirm = () => {
    if (isEmpty || isClientCollision) {
      return;
    }
    onConfirm(trimmedName);
  };

  return (
    <Dialog open={open} onClose={onCancel} maxWidth={false} slotProps={{ paper: { sx: dialogPaperSx } }}>
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          fontSize: 16,
          letterSpacing: '0.15px',
          lineHeight: 1.5,
          color: alpha(colors.primary[100], 0.87),
          pt: '16px',
          pb: '10px',
          mb: '12px',
          px: DIALOG_PX,
        }}
      >
        Duplicate List
        <IconButton size='small' aria-label='Close' onClick={onCancel} disabled={loading}>
          <CloseIcon sx={{ fontSize: 20, color: colors.neutral[40] }} />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: '12px', px: DIALOG_PX }}>
        <div>
          <Typography variant='caption' color='text.secondary' display='block' sx={{ mb: '4px' }}>
            New List Name <span style={{ color: colors.critical[50] }}>*</span>
          </Typography>
          <OutlinedInput
            fullWidth
            autoFocus
            value={name}
            onChange={(e) => handleNameChange(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                handleConfirm();
              }
            }}
            disabled={loading}
            error={Boolean(inlineError)}
            sx={dialogInputSx}
          />
          {inlineError && (
            <Typography variant='caption' display='block' sx={{ mt: '4px', color: colors.critical[50] }}>
              {inlineError}
            </Typography>
          )}
        </div>
      </DialogContent>
      <DialogActions sx={{ justifyContent: 'flex-start', gap: '4px', py: '24px', px: DIALOG_PX }}>
        <FooterButtons
          confirmButtonText='Duplicate'
          onConfirm={handleConfirm}
          onCancel={onCancel}
          onConfirmDisabled={isEmpty || isClientCollision}
          loading={loading}
        />
      </DialogActions>
    </Dialog>
  );
};
